import type { ImageTypeT } from "@/app/types/images";

type ImageMetadataProps = {
  isUploading: boolean;
  currentImageUrl?: string | null;
  imageType?: ImageTypeT;
  // Multi-image support
  imageCount?: number;
  maxImages?: number;
  // File name support
  fileName?: string | null;
};

/**
 * Reusable image metadata display component
 *
 * Shows upload status and image type information in a clean,
 * consistent format. Handles different loading states and
 * supports all image types (posts, contact_persons, etc.)
 * with localized labels. Supports both single and multi-image
 * upload scenarios.
 */
export function ImageMetadata({
  isUploading,
  currentImageUrl,
  imageType,
  imageCount,
  maxImages,
  fileName,
}: ImageMetadataProps) {
  const getStatusText = () => {
    if (isUploading) return "Wird hochgeladen...";

    // Multi-image support
    if (imageCount !== undefined) {
      if (imageCount === 0) return "Keine Bilder";
      if (imageCount === 1) return "1 Bild hochgeladen";
      return `${imageCount} Bilder hochgeladen`;
    }

    // Single-image support (backward compatibility)
    if (currentImageUrl) return "Hochgeladen";
    return "Kein Bild";
  };

  const getImageTypeText = () => {
    if (!imageType) return "Bild";

    switch (imageType) {
      case "header":
        return "Header";
      case "thumbnail":
        return "Thumbnail";
      case "profile":
        return "Profilbild";
      case "additional":
        return "Weitere Bilder";
      default:
        return "Bild";
    }
  };

  const getImageCountText = () => {
    if (imageCount === undefined || maxImages === undefined) return null;
    return `${imageCount}/${maxImages}`;
  };

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col justify-start pt-4">
      <div className="flex min-w-0 flex-col justify-start space-y-1 text-xs text-gray-500">
        <div className="flex min-w-0 justify-start gap-1">
          <span className="shrink-0 text-gray-500">Status:</span>
          <span className="min-w-0 truncate font-medium text-gray-700">
            {getStatusText()}
          </span>
        </div>
        <div className="flex justify-start gap-1 text-xs">
          <span className="shrink-0 text-gray-500">Typ:</span>
          <span className="font-medium text-gray-700">
            {getImageTypeText()}
          </span>
        </div>
        {/* Show image count for multi-image uploads */}
        {getImageCountText() && (
          <div className="flex justify-start gap-1 text-xs">
            <span className="shrink-0 text-gray-500">Anzahl:</span>
            <span className="font-medium text-gray-700">
              {getImageCountText()}
            </span>
          </div>
        )}
        {/* Show file name if available */}
        {fileName && (
          <div className="flex min-w-0 justify-start gap-1 text-xs">
            <span className="shrink-0 text-gray-500">Name:</span>
            <span className="min-w-0 truncate font-medium text-gray-700">
              {fileName}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
